import React, { useState, useEffect, useCallback } from 'react';
import { Spin, Tag } from 'antd';
import {
    VideoCameraOutlined,
    LockOutlined,
    InboxOutlined,
} from '@ant-design/icons';
import {
    ResponsiveContainer,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    PieChart,
    Pie,
    Cell,
} from 'recharts';
import moment from 'moment';
import { useReports } from '../context/ReportsContext';
import { getMeetingsReport } from '../api/reportsApi';
import { TabHelpButton } from '../components/TabHelpGuideModal';

const PALETTE = ['#00AAF1', '#10b981', '#6366f1', '#a855f7', '#f59e0b', '#ec4899'];
const tooltipStyle = {
    contentStyle: { background: '#0a0a0a', border: '1px solid #262626', borderRadius: 12, fontSize: 12, color: '#fff' },
    labelStyle: { color: '#a7c8ff', fontWeight: 700 },
};
const axisTick = { fontSize: 12, fill: '#94a3b8' };

export default function MeetingsTab() {
    const { filters, refreshKey, getCachedTab, setCachedTab } = useReports();
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState(() => getCachedTab('meetings'));

    const loadData = useCallback(async () => {
        const cached = getCachedTab('meetings');
        if (cached) {
            setData(cached);
            return;
        }

        setLoading(true);
        try {
            const res = await getMeetingsReport({
                period: filters.period,
                startDate: filters.startDate,
                endDate: filters.endDate,
                teamMember: filters.teamMember,
                department: filters.department,
                projectId: filters.projectId,
            });
            if (res && res.success && res.result) {
                setData(res.result);
                setCachedTab('meetings', res.result);
            }
        } catch (err) {
            console.error('Error fetching meetings report:', err);
        } finally {
            setLoading(false);
        }
    }, [filters, refreshKey, getCachedTab, setCachedTab]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const activeData = data || {};
    const hasMeetings = Boolean(activeData?.meetings && activeData.meetings.length > 0);

    const projectInfo = activeData?.project || {
        name: 'DymaxTech Team Meeting Minutes',
        projectCode: 'PRJ-9246',
    };

    const categories = activeData?.categories || [];
    const dailyDensity = (activeData?.dailyDensity || []).map(d => ({
        ...d,
        label: d.label || moment(d.date).format('ddd D'),
    }));
    const meetings = activeData?.meetings || [];
    const totalHours = activeData?.totalMeetingHours || '0h';
    const totalActionItems = activeData?.totalActionItems || 0;



    const nonZeroCategories = categories.filter(c => c.minutes > 0 || c.sessions > 0);

    return (
        <Spin spinning={loading} size="large" tip="Loading meeting analytics...">
            <div className="space-y-6 min-h-[400px]">
            {/* PRJ-9246 Dedicated Project System Banner */}
            <div className="p-4 bg-gradient-to-r from-blue-50/80 to-indigo-50/80 dark:from-black dark:to-neutral-950 rounded-2xl border border-blue-200/70 dark:border-neutral-800 shadow-2xs flex flex-col md:flex-row md:items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-blue-600 text-white flex items-center justify-center font-bold text-base shadow-xs">
                        <VideoCameraOutlined />
                    </div>
                    <div>
                        <div className="flex items-center gap-2">
                            <span className="text-xs font-black uppercase tracking-wider px-2 py-0.5 rounded-md bg-blue-600 text-white font-headline">
                                {projectInfo.projectCode || projectInfo.code || 'PRJ-9246'}
                            </span>
                            <h3 className="text-base font-extrabold text-[#003263] dark:text-[#a7c8ff] font-headline">
                                {projectInfo.name || 'DymaxTech Team Meeting Minutes'}
                            </h3>
                        </div>
                        <p className="text-xs text-slate-600 dark:text-slate-300 mt-0.5">
                            Each milestone in this project defines the meeting category (e.g. Daily Dev, Client, Tech Sync). Tasks track individual sessions and action items.
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-2 text-xs font-bold text-slate-500 dark:text-slate-400">
                    <span className="px-2.5 py-1 rounded-lg bg-white dark:bg-black border border-slate-200 dark:border-neutral-800 flex items-center gap-1.5">
                        <LockOutlined className="text-blue-500" />
                        <span>Isolated from Billable Client Effort</span>
                    </span>
                    <TabHelpButton tabKey="meetings" />
                </div>
            </div>

            {/* Milestone Category Stat Cards */}
            {categories.length > 0 ? (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                    {categories.map((cat, idx) => {
                        const color = PALETTE[idx % PALETTE.length];
                        return (
                            <div
                                key={cat.name}
                                className="p-4 rounded-2xl bg-white dark:bg-black border border-slate-200/80 dark:border-neutral-800 shadow-xs"
                            >
                                <div className="flex items-center justify-between mb-1.5">
                                    <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">Milestone</span>
                                    <span className="w-2.5 h-2.5 rounded-full" style={{ background: color }}></span>
                                </div>
                                <div className="text-sm font-extrabold text-slate-900 dark:text-white line-clamp-1 font-headline">
                                    {cat.name}
                                </div>
                                <div className="text-xl font-black font-headline mt-1.5" style={{ color }}>
                                    {cat.hours}{' '}
                                    <span className="text-xs text-slate-400 font-semibold font-sans">
                                        ({cat.sessions} {cat.sessions === 1 ? 'session' : 'sessions'})
                                    </span>
                                </div>
                            </div>
                        );
                    })}
                </div>
            ) : (
                <div className="p-6 rounded-2xl bg-white dark:bg-black border border-slate-200/80 dark:border-neutral-800 text-center text-xs text-slate-400">
                    Project <strong>PRJ-9246 (DymaxTech Team Meeting Minutes)</strong> has no milestones or sessions in the active database yet. When connected to the live database, milestone categories will populate automatically.
                </div>
            )}

            {/* Visual Charts: Milestone Category Donut + Daily Meeting Density Bar */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Milestone Category Distribution Donut */}
                <div className="bg-white dark:bg-black p-6 rounded-2xl border border-slate-200/80 dark:border-neutral-800 shadow-xs flex flex-col justify-between">
                    <div>
                        <h3 className="text-base font-bold text-slate-900 dark:text-white font-headline">
                            Time by Milestone Category
                        </h3>
                        <p className="text-xs text-slate-400 mb-4">
                            Total tracked meeting hours: <strong className="text-slate-700 dark:text-slate-200">{totalHours}</strong>
                        </p>
                    </div>

                    {nonZeroCategories.length === 0 ? (
                        <div className="h-52 flex flex-col items-center justify-center text-slate-400 text-xs gap-2">
                            <InboxOutlined style={{ fontSize: 22 }} />
                            <span>No meeting hours recorded yet</span>
                        </div>
                    ) : (
                        <>
                            <div className="h-52">
                                <ResponsiveContainer width="100%" height="100%">
                                    <PieChart>
                                        <Pie
                                            data={nonZeroCategories}
                                            dataKey="minutes"
                                            nameKey="name"
                                            innerRadius="58%"
                                            outerRadius="85%"
                                            paddingAngle={2}
                                            stroke="none"
                                        >
                                            {nonZeroCategories.map((c, i) => (
                                                <Cell key={c.name} fill={PALETTE[i % PALETTE.length]} />
                                            ))}
                                        </Pie>
                                        <Tooltip {...tooltipStyle} />
                                    </PieChart>
                                </ResponsiveContainer>
                            </div>

                            <div className="space-y-2 text-xs font-semibold mt-2">
                                {categories.map((c, i) => (
                                    <div key={c.name} className="flex justify-between items-center text-slate-700 dark:text-slate-300">
                                        <span className="flex items-center gap-2 truncate">
                                            <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: PALETTE[i % PALETTE.length] }} />
                                            <span className="truncate">{c.name}</span>
                                        </span>
                                        <span className="font-extrabold">{c.percentage}% ({c.hours})</span>
                                    </div>
                                ))}
                            </div>
                        </>
                    )}
                </div>

                {/* Daily Meeting Density Bar Chart */}
                <div className="lg:col-span-2 bg-white dark:bg-black p-6 rounded-2xl border border-slate-200/80 dark:border-neutral-800 shadow-xs">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-4">
                        <div>
                            <h3 className="text-base font-bold text-slate-900 dark:text-white font-headline">
                                Daily Meeting Load &amp; Action Items
                            </h3>
                            <p className="text-xs text-slate-400">
                                Meeting hours and sessions logged per day under PRJ-9246
                            </p>
                        </div>
                        <span className="text-xs font-bold text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-neutral-900 px-3 py-1.5 rounded-xl border border-indigo-200/50 dark:border-neutral-700">
                            {totalActionItems} Action Items Generated
                        </span>
                    </div>

                    <div className="h-60">
                        {dailyDensity.length === 0 ? (
                            <div className="h-full flex flex-col items-center justify-center text-slate-400 text-xs gap-2">
                                <InboxOutlined style={{ fontSize: 22 }} />
                                <span>No meeting sessions in the selected window</span>
                            </div>
                        ) : (
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={dailyDensity} margin={{ top: 8, right: 8, left: -12, bottom: 0 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" strokeOpacity={0.25} vertical={false} />
                                    <XAxis dataKey="label" tick={axisTick} axisLine={false} tickLine={false} />
                                    <YAxis tick={axisTick} axisLine={false} tickLine={false} unit="h" />
                                    <Tooltip {...tooltipStyle} />
                                    <Legend wrapperStyle={{ fontSize: 12 }} />
                                    <Bar dataKey="hours" name="Meeting Hours" fill="#00AAF1" radius={[6, 6, 0, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        )}
                    </div>
                </div>
            </div>

            {/* Recent Recorded Meeting Sessions Visual Cards */}
            {meetings.length > 0 && (
                <div className="bg-white dark:bg-black p-6 rounded-2xl border border-slate-200/80 dark:border-neutral-800 shadow-xs">
                    <h3 className="text-base font-bold text-slate-900 dark:text-white font-headline mb-4">
                        Logged Sessions &amp; Action Item Deliverables
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {meetings.slice(0, 9).map((m) => (
                            <div key={m.id} className="p-4 rounded-xl bg-slate-50/70 dark:bg-neutral-950 border border-slate-200/60 dark:border-neutral-800 space-y-2.5">
                                <div className="flex items-center justify-between">
                                    <Tag color="blue">{m.category || 'Meeting'}</Tag>
                                    <span className="text-xs text-slate-400 font-semibold">{m.duration}</span>
                                </div>
                                <h4 className="text-sm font-bold text-slate-900 dark:text-white line-clamp-1">{m.title}</h4>
                                <div className="flex items-center justify-between text-xs text-slate-500 dark:text-slate-400">
                                    <span>{m.attendeesCount} participants</span>
                                    <span className="font-bold text-indigo-600 dark:text-indigo-400">{m.actionItemsCount} subtasks</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
            </div>
        </Spin>
    );
}
